import { type PendingApprovalResolutionType } from "#src/view-models/investigations";
import { RichTextMarkdownDataDisplay } from "@validereinc/common-components";
import {
  ApprovalDecision,
  InvestigationMessageKind,
  InvestigationMessageRole,
  type InvestigationMessageType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import { CopyableConversationMessageBubble } from "./CopyableConversationMessageBubble";
import {
  PendingApprovalCard,
  type PendingApprovalCardDecisionType,
} from "./PendingApprovalCard";
import { resolveApprovalCardContent } from "./ApprovalCardContent";
import { getPendingApprovalAccompanyingAgentMessage } from "./ApprovalCardContent.helpers";
import { ExecuteWorkflowTerminalMessageContent } from "./ExecuteWorkflowTerminalMessageContent";
import { parseExecuteWorkflowToolResultMetadata } from "./executeWorkflowMessage.helpers";
import { useCanSubmitInvestigationWriteApproval } from "./useCanSubmitInvestigationWriteApproval";
import styles from "./InvestigationDetailPage.module.scss";

const cx = classNames.bind(styles);

export type ConversationMessageRenderContextType = {
  pendingApprovalMessageId: string | null;
  resolution?: PendingApprovalResolutionType;
  isApprovalLoading: boolean;
  onApprovalDecision?: (decision: PendingApprovalCardDecisionType) => void;
};

/**
 * Whether the feed should render a bubble wrapper for this message row.
 */
export const shouldRenderConversationMessageBubble = (
  message: InvestigationMessageType
): boolean => {
  if (message.role === InvestigationMessageRole.USER) {
    return Boolean(message.content.trim());
  }

  if (message.kind === InvestigationMessageKind.PENDING_APPROVAL) {
    return true;
  }

  return Boolean(message.content.trim());
};

const PendingApprovalConversationContent = ({
  message,
  pendingApprovalMessageId,
  resolution,
  isApprovalLoading,
  onApprovalDecision,
}: {
  message: InvestigationMessageType;
} & ConversationMessageRenderContextType) => {
  const [canSubmitWriteApproval, isWriteApprovalAuthLoading] =
    useCanSubmitInvestigationWriteApproval();
  const isOpenPendingApproval = message.id === pendingApprovalMessageId;
  const accompanyingAgentMessage = getPendingApprovalAccompanyingAgentMessage(
    message.content
  );
  const hitlEnvelopeContent = message.content.trim();

  return (
    <CopyableConversationMessageBubble
      content={hitlEnvelopeContent}
      isUserMessage={false}
      bubbleClassName={cx("conversation-feed__pending-approval")}
      testId={`conversation-message-${message.role}`}
    >
      {accompanyingAgentMessage ? (
        <div
          className={cx(
            "conversation-feed__bubble",
            "conversation-feed__bubble--agent",
            "conversation-feed__pending-approval-accompanying-message"
          )}
        >
          <RichTextMarkdownDataDisplay
            value={accompanyingAgentMessage}
            isInline={false}
            isContainerSizing
            isConstrainedToParentWidth
            className={cx("conversation-feed__bubble__body")}
            markdownContainerClassName={cx(
              "conversation-feed__bubble__markdown"
            )}
          />
        </div>
      ) : null}
      <PendingApprovalCard
        decision={resolution?.decision ?? undefined}
        isResolved={Boolean(resolution)}
        isReadOnly={!isOpenPendingApproval}
        isDecisionPermitted={canSubmitWriteApproval}
        isDecisionPermissionLoading={isWriteApprovalAuthLoading}
        isLoading={isApprovalLoading}
        onDecide={(decision) => {
          if (!onApprovalDecision) return;
          if (
            decision !== ApprovalDecision.APPROVE &&
            decision !== ApprovalDecision.REJECT
          ) {
            return;
          }
          onApprovalDecision(decision);
        }}
      >
        {resolveApprovalCardContent(message.metadata)}
      </PendingApprovalCard>
    </CopyableConversationMessageBubble>
  );
};

/**
 * Renders the inner body for a conversation message (user text, agent markdown, or approval card).
 */
export const ConversationMessageBubbleContent = ({
  message,
  pendingApprovalMessageId,
  resolution,
  isApprovalLoading,
  onApprovalDecision,
}: {
  message: InvestigationMessageType;
} & ConversationMessageRenderContextType) => {
  const trimmedContent = message.content.trim();

  if (
    message.role === InvestigationMessageRole.AGENT &&
    message.kind === InvestigationMessageKind.PENDING_APPROVAL
  ) {
    return (
      <PendingApprovalConversationContent
        message={message}
        pendingApprovalMessageId={pendingApprovalMessageId}
        resolution={resolution}
        isApprovalLoading={isApprovalLoading}
        onApprovalDecision={onApprovalDecision}
      />
    );
  }

  if (message.role === InvestigationMessageRole.AGENT) {
    const executeWorkflowToolResult = parseExecuteWorkflowToolResultMetadata(
      message.metadata
    );
    if (
      message.kind === InvestigationMessageKind.MESSAGE &&
      executeWorkflowToolResult !== undefined
    ) {
      return (
        <ExecuteWorkflowTerminalMessageContent
          content={trimmedContent}
          toolResultMetadata={executeWorkflowToolResult}
        />
      );
    }

    return (
      <RichTextMarkdownDataDisplay
        value={trimmedContent}
        isInline={false}
        isContainerSizing
        isConstrainedToParentWidth
        className={cx("conversation-feed__bubble__body")}
        markdownContainerClassName={cx("conversation-feed__bubble__markdown")}
      />
    );
  }

  return (
    <div className={cx("conversation-feed__bubble__body")}>
      {trimmedContent}
    </div>
  );
};
